<div class="dashboard-container">
  <!-- Daywise Metrics Filter -->
  <div class="top-grid daywise-filter">
    @if (!editView) {
      <p class="m-0">Displaying energy usage insights from <strong>{{ fromDate | date: 'dd/MM/yyyy' }} </strong> to <strong>{{ toDate | date: 'dd/MM/yyyy' }}</strong></p>
      <button mat-button color="primary" (click)="editView = true">Change</button>
    } @else {
      <!-- Date Range Picker -->
      <mat-form-field appearance="outline" [subscriptSizing]="'dynamic'" class="date-range-picker">
        <mat-label>Enter date range</mat-label>
        <mat-date-range-input [formGroup]="range" [rangePicker]="picker" [min]="minDate" [max]="maxDate">
          <input matStartDate formControlName="start" placeholder="Start date">
          <input matEndDate formControlName="end" placeholder="End date">
        </mat-date-range-input>
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-date-range-picker #picker></mat-date-range-picker>

        <mat-error>@if (range.controls.start.hasError('matStartDateInvalid')) {Invalid start date}</mat-error>
        <mat-error>@if (range.controls.end.hasError('matEndDateInvalid')) {Invalid end date}</mat-error>
      </mat-form-field>

      <button mat-raised-button color="primary" (click)="applyDateChange()" [disabled]="!range.valid"
        class="apply-button">Apply</button>
      <button mat-button color="primary" (click)="editView = false">Cancel</button>
    }
  </div>

  <!-- Main Metrics Cards (Daywise) -->
  <div class="main-metrics-grid daywise-metrics">
    <app-metrics-card class="metrics-card"
      [value]="metrics.totalSwitches.value"
      [trend]="metrics.totalSwitches.trend"
      [subValues]="[
        { label: 'Retention', value: metrics.retentionSwitches.value ?? 0 },
        { label: 'Acquisition', value: metrics.acquisitionSwitches.value ?? 0 },
        { label: 'Dual fuel', value: metrics.dualFuelSwitches.value ?? 0 },
        { label: 'Elec only', value: metrics.elecOnlySwitches.value ?? 0 }
      ]"
      label="Total Switches">
    </app-metrics-card>

    <app-metrics-card class="metrics-card"
      [value]="metrics.totalMPANs.value"
      [trend]="metrics.totalMPANs.trend"
      [subValues]="[{ label: 'Smart Meters', value: metrics.smartMeterMPANs.value ?? 0 }]" 
      label="Total MPANs">
    </app-metrics-card>
    
    <app-metrics-card class="metrics-card"
      [value]="metrics.totalMPRNs.value"
      [trend]="metrics.totalMPRNs.trend"
      [subValues]="[{ label: 'Smart Meters', value: metrics.smartMeterMPRNs.value ?? 0 }]" 
      label="Total MPRNs">
    </app-metrics-card>

    <app-metrics-card class="metrics-card"
      [value]="metrics.totalCombinedUsage.value"
      [trend]="metrics.totalCombinedUsage.trend"
      suffix="kWh"
      [subValues]="[
        { label: 'Electricity (kWh)', value: metrics.avgElectricityUsage.value ?? 0 },
        { label: 'Gas (kWh)', value: metrics.avgGasUsage.value ?? 0 }
      ]" 
      label="Yearly Average Consumption">
    </app-metrics-card>
  </div>

  <!-- Charts Section (Daywise) -->
  <div class="charts-section daywise-metrics">
    <!-- Profile Class Chart -->
    @if (isChartsLoading || profileClassChart.series) {
      <mat-card class="chart-card" appearance="outlined">
        <mat-card-content>
          <div *ngIf="isChartsLoading" class="loading">Loading chart data...</div>
          <div *ngIf="!isChartsLoading && !profileClassChart.series" class="no-data">No data available</div>
          <highcharts-chart *ngIf="!isChartsLoading && profileClassChart.series" 
            [Highcharts]="Highcharts"
            [constructorType]="chartConstructor" 
            [options]="profileClassChart" 
            [update]="updateFlag">
          </highcharts-chart>
        </mat-card-content>
      </mat-card>
    }

    <!-- Dual Fuel Usage Chart -->
    @if (isChartsLoading || dualFuelUsageChart.series) {
      <mat-card class="chart-card" appearance="outlined">
        <mat-card-content>
          <div *ngIf="isChartsLoading" class="loading">Loading chart data...</div>
          <div *ngIf="!isChartsLoading && !dualFuelUsageChart.series" class="no-data">No data available</div>
          <highcharts-chart *ngIf="!isChartsLoading && dualFuelUsageChart.series" 
            [Highcharts]="Highcharts"
            [constructorType]="chartConstructor" 
            [options]="dualFuelUsageChart" 
            [update]="updateFlag">
          </highcharts-chart>
        </mat-card-content>
      </mat-card>
    }

    <!-- Electricity Usage Chart -->
    @if (isChartsLoading || electricityUsageChart.series) {
      <mat-card class="chart-card" appearance="outlined">
        <mat-card-content>
          <div *ngIf="isChartsLoading" class="loading">Loading chart data...</div>
          <div *ngIf="!isChartsLoading && !electricityUsageChart.series" class="no-data">No data available</div>
          <highcharts-chart *ngIf="!isChartsLoading && electricityUsageChart.series" 
            [Highcharts]="Highcharts"
            [constructorType]="chartConstructor" 
            [options]="electricityUsageChart" 
            [update]="updateFlag">
          </highcharts-chart>
        </mat-card-content>
      </mat-card>
    }
  </div>

  <!-- Visual Separator -->
  <div class="metrics-separator"></div>

  <!-- Annual Metrics Filter -->
  <div class="top-grid annual-filter">
    @if (!annualEditView) {
      <p class="m-0">Displaying annual metrics for <strong>{{ annualFromDate | date: 'dd/MM/yyyy' }} </strong> to <strong>{{ annualToDate | date: 'dd/MM/yyyy' }}</strong></p>
      <button mat-button color="primary" (click)="enterAnnualEditMode()">Change</button>
    } @else {
      <!-- Period Range Picker Component -->
      <app-period-range-picker 
        [(ngModel)]="periodRange"
        [minDate]="annualMinDate"
        [maxDate]="annualMaxDate"
        (ngModelChange)="onPeriodRangeChange($event)">
      </app-period-range-picker>

      <button mat-raised-button color="primary" (click)="applyAnnualDateChange()" 
        [disabled]="!periodRange"
        class="apply-button">Apply</button>
      <button mat-button color="primary" (click)="cancelAnnualEdit()">Cancel</button>
    }
  </div>

  <!-- Annual Metrics Content -->
  <div class="content-sections annual-metrics">
    <!-- Loading State -->
    @if (isLoading) {
      <div class="loading">Loading energy usage data...</div>
    }

    <!-- Grouped Metrics Sections -->
    @if (!isLoading) {
      <div class="metrics-sections">
        <!-- Savings Section -->
        @if (metricGroups.savings.length > 0) {
          <div class="metrics-section">
            <h3 class="section-title">Saving customers &pound; via energy procurement</h3>
            <div class="metrics-grid">
              @for (metric of metricGroups.savings; track metric.type) {
                <app-metrics-card
                  class="metrics-card"
                  [value]="metric.value"
                  [trend]="metric.trend"
                  [label]="metric.label"
                  [description]="metric.description"
                  [isCurrency]="true">
                </app-metrics-card>
              }
            </div>
          </div>
        }

        <!-- Usage Section -->
        @if (metricGroups.usage.length > 0) {
          <div class="metrics-section">
            <h3 class="section-title"># GW of energy procured through our system (based on submitted K's)</h3>
            <div class="metrics-grid">
              @for (metric of metricGroups.usage; track metric.type) {
                <app-metrics-card
                  class="metrics-card"
                  [value]="metric.value"
                  [trend]="metric.trend"
                  [label]="metric.label"
                  [description]="metric.description"
                >
                </app-metrics-card>
              }
            </div>
          </div>
        }

        <!-- Carbon Footprint Section -->
        @if (metricGroups.carbonFootprint.length > 0) {
          <div class="metrics-section">
            <h3 class="section-title"># tonnes CO2 saved from switching to greener tariff</h3>
            <div class="metrics-grid">
              @for (metric of metricGroups.carbonFootprint; track metric.type) {
                <app-metrics-card
                  class="metrics-card"
                  [value]="metric.value"
                  [trend]="metric.trend"
                  [label]="metric.label"
                  [description]="metric.description"
                  >
                </app-metrics-card>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>
</div>
